import LinkExtension from "@tiptap/extension-link";
import type { Editor, JSONContent } from "@tiptap/core";
import { TextSelection } from "@tiptap/pm/state";
import { EditorContent, useEditor, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Archive,
  BadgePlus,
  BookOpen,
  Bold,
  CalendarPlus,
  Check,
  Clock3,
  ExternalLink,
  FileDown,
  Heading1,
  Heading2,
  ImageOff,
  Italic,
  List,
  PencilLine,
  Plus,
  RotateCcw,
  Star,
  Trash2,
} from "lucide-react";
import { Fragment, useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";

import {
  richTextDocumentSchema,
  type ActivityDetail,
  type ActivityMaterial,
  type FlashcardOptionsResponse,
  type LessonFlashcardsResponse,
  type RichTextDocument,
  type RichTextMark,
  type RichTextNode,
  type ActivitySession,
  youtubeEmbedUrl,
} from "@learn-flow/contracts";

import { AppShell } from "../../components/app-shell.js";
import { UndoCompletionDialog } from "../../components/undo-completion-dialog.js";
import {
  createActivityReturnState,
  resolveActivityReturnContext,
  type ActivityReturnContext,
} from "../../lib/activity-return-context.js";
import { ApiError } from "../../lib/api.js";
import { createUuid } from "../../lib/random-uuid.js";
import { ArchiveActivityDialog } from "../library/archive-activity-dialog.js";
import { libraryQueryRoot } from "../library/library-api.js";
import { readStoredLibraryContext } from "../library/library-context.js";
import { loadToday, markActivityOpened, todayQueryRoot } from "../today/today-api.js";
import {
  activityDetailKey,
  checkUploadAvailability,
  cancelActivitySession,
  completeActivitySession,
  loadActivityDetail,
  saveActivityNote,
  setActivityFavorite,
  undoActivitySession,
} from "./activity-api.js";
import {
  compareActivitySessions,
  selectPlannedActivitySessions,
} from "./activity-session-banner-model.js";
import { CompletionReflectionDialog } from "./completion-reflection-dialog.js";
import { DeleteSessionDialog } from "./delete-session-dialog.js";
import { FlashcardDialog } from "./flashcard-dialog.js";
import { flashcardOptionsKey, lessonFlashcardsKey, loadFlashcardOptions } from "./flashcard-api.js";
import { LessonFlashcardsPanel } from "./lesson-flashcards-panel.js";

type ActivityRouteState = {
  activityTitle?: string;
  schedulingNotice?: string;
};

type ActivityEditRouteState = {
  activityDetailReturnTo: string;
  activityReturnTo?: string;
  libraryReturnTo: string;
};

const emptyDocument: RichTextDocument = {
  type: "doc",
  content: [{ type: "paragraph" }],
};

const noteAutosaveDebounceMs = 750;

type NoteSaveAttempt = {
  document: RichTextDocument;
  revision: number;
  version: number | null;
};

type NoteTextSelection = {
  from: number;
  to: number;
  text: string;
};

export function ActivityDetailPage(): React.JSX.Element {
  const { activityId = "" } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();
  const openedActivity = useRef("");
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const routeState = isActivityRouteState(location.state) ? location.state : {};
  const returnContext = resolveActivityReturnContext(location.state, readStoredLibraryContext());
  const returnTo = returnContext.destination;
  const editRouteState: ActivityEditRouteState = {
    ...createActivityReturnState(returnTo),
    activityDetailReturnTo: `${location.pathname}${location.search}`,
    libraryReturnTo: returnTo,
  };
  const detail = useQuery({
    queryKey: activityDetailKey(activityId),
    queryFn: () => loadActivityDetail(activityId),
    enabled: Boolean(activityId),
  });
  const favoriteMutation = useMutation({
    mutationFn: (favorite: boolean) => setActivityFavorite(activityId, favorite),
    onSuccess: async (result) => {
      queryClient.setQueryData<ActivityDetail>(activityDetailKey(activityId), (current) =>
        current ? { ...current, favoriteAt: result.favoriteAt } : current,
      );
      await queryClient.invalidateQueries({ queryKey: libraryQueryRoot });
    },
  });

  useEffect(() => {
    document.title = `${detail.data?.title ?? routeState.activityTitle ?? "Activity"} · MemoKite`;
  }, [detail.data?.title, routeState.activityTitle]);
  useEffect(() => {
    if (!detail.data || openedActivity.current === detail.data.id) return;
    openedActivity.current = detail.data.id;
    void markActivityOpened(detail.data.id)
      .then(() => queryClient.invalidateQueries({ queryKey: todayQueryRoot }))
      .catch(() => {
        openedActivity.current = "";
      });
  }, [detail.data, queryClient]);
  if (detail.isPending) {
    return (
      <DetailShell returnContext={returnContext}>
        <div className="activity-detail-loading" aria-busy="true" aria-label="Loading activity">
          <span />
          <span />
          <span />
        </div>
      </DetailShell>
    );
  }
  if (detail.isError) {
    const missing = detail.error instanceof ApiError && detail.error.status === 404;
    return (
      <DetailShell returnContext={returnContext}>
        <section className="activity-detail-empty" role={missing ? undefined : "alert"}>
          <BookOpen size={30} />
          <h1>{missing ? "Activity not found" : "This activity could not be loaded"}</h1>
          <p>
            {missing
              ? "It may have been removed, or it may not belong to this account."
              : "Your learning content is safe. Try loading it again."}
          </p>
          {missing ? (
            <Link className="soft-button" to={returnTo}>
              Back to {returnContext.label}
            </Link>
          ) : (
            <button type="button" className="soft-button" onClick={() => void detail.refetch()}>
              Try again
            </button>
          )}
        </section>
      </DetailShell>
    );
  }

  const activity = detail.data;
  return (
    <DetailShell returnContext={returnContext}>
      <header className="activity-detail-hero">
        <div className="activity-detail-heading">
          <div className="activity-detail-kicker">
            <span>{formatLabel(activity.format)}</span>
            {activity.collection ? <span>{activity.collection.name}</span> : null}
          </div>
          <h1>{activity.title}</h1>
        </div>
        <div className="activity-detail-actions">
          <button
            type="button"
            className={activity.favoriteAt ? "favorite-button is-favorite" : "favorite-button"}
            aria-pressed={Boolean(activity.favoriteAt)}
            disabled={favoriteMutation.isPending}
            onClick={() => favoriteMutation.mutate(!activity.favoriteAt)}
          >
            <Star size={17} fill={activity.favoriteAt ? "currentColor" : "none"} />
            {activity.favoriteAt ? "Favorited" : "Favorite"}
          </button>
          <div className="activity-detail-management-actions">
            <Link
              className="soft-button"
              to={`/activities/${activity.id}/edit`}
              state={editRouteState}
            >
              <PencilLine size={16} /> Edit
            </Link>
            {activity.archivedAt === null ? (
              <button
                type="button"
                className="danger-button"
                onClick={() => setArchiveDialogOpen(true)}
              >
                <Archive size={16} /> Archive
              </button>
            ) : null}
          </div>
          {favoriteMutation.isError ? (
            <span className="mutation-error" role="alert">
              Favorite was not changed. Try again.
            </span>
          ) : null}
        </div>
      </header>

      {activity.archivedAt ? (
        <div
          className="activity-archive-status"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          <Archive size={20} aria-hidden="true" />
          <span>This activity is archived.</span>
        </div>
      ) : null}

      <ActivitySessionBanner activity={activity} returnTo={returnTo} />

      {routeState.schedulingNotice ? (
        <div className="activity-schedule-notice" role="status">
          <CalendarPlus size={16} /> {routeState.schedulingNotice}
        </div>
      ) : null}

      <div className="activity-detail-layout">
        <section className="activity-material-column" aria-labelledby="activity-materials-heading">
          <div className="activity-section-heading">
            {activity.summary ? (
              <section
                className="activity-learning-intention"
                aria-labelledby="activity-learning-intention-heading"
              >
                <h2
                  id="activity-learning-intention-heading"
                  className="activity-learning-intention-heading"
                >
                  Learning intention
                </h2>
                <p>{activity.summary}</p>
              </section>
            ) : null}
            <span className="section-kicker">Learning material</span>
            <h2 id="activity-materials-heading">Work through this activity</h2>
          </div>
          <div className="activity-material-list">
            {activity.materials.map((material, index) => (
              <MaterialBlock
                key={material.id}
                activityId={activity.id}
                material={material}
                position={index + 1}
                editHref={`/activities/${activity.id}/edit`}
                editRouteState={editRouteState}
              />
            ))}
          </div>
        </section>
        <aside className="activity-notes-column">
          <NotesPanel key={activity.id} activity={activity} />
        </aside>
      </div>
      {archiveDialogOpen ? (
        <ArchiveActivityDialog
          activity={activity}
          onArchived={async (result) => {
            queryClient.setQueryData<ActivityDetail>(activityDetailKey(activityId), (current) =>
              current ? { ...current, archivedAt: result.archivedAt } : current,
            );
            await queryClient.invalidateQueries({ queryKey: activityDetailKey(activityId) });
          }}
          onDismiss={() => setArchiveDialogOpen(false)}
        />
      ) : null}
    </DetailShell>
  );
}

function DetailShell({
  returnContext,
  children,
}: {
  returnContext: ActivityReturnContext;
  children: React.ReactNode;
}) {
  return (
    <AppShell mainClassName="activity-detail-screen" sidebarCollapsible>
      <nav className="activity-detail-topbar" aria-label="Activity navigation">
        <Link className="back-button" to={returnContext.destination}>
          <ArrowLeft size={17} /> Back to {returnContext.label}
        </Link>
      </nav>
      {children}
    </AppShell>
  );
}

function MaterialBlock({
  activityId,
  material,
  position,
  editHref,
  editRouteState,
}: {
  activityId: string;
  material: ActivityMaterial;
  position: number;
  editHref: string;
  editRouteState: ActivityEditRouteState;
}) {
  if (material.kind === "link") {
    return (
      <ExternalMaterialBlock activityId={activityId} material={material} position={position} />
    );
  }
  if (material.kind === "rich_text") {
    return (
      <article className="material-block">
        <div className="material-block-body rich-text-content">
          <MaterialKind position={position}>Reading notes</MaterialKind>
          <RichText document={material.document} />
        </div>
      </article>
    );
  }
  const available = material.file.status === "ready" && Boolean(material.file.contentUrl);
  if (!available) {
    return (
      <article className="material-block material-unavailable" role="status">
        <div className="material-block-body">
          <ImageOff size={22} />
          <MaterialKind position={position}>
            {material.kind === "image" ? "Image unavailable" : "File unavailable"}
          </MaterialKind>
          <h3>{material.file.originalFilename}</h3>
          <p>
            This resource was removed or is temporarily unavailable. Other materials still work.
          </p>
          <Link to={editHref} state={editRouteState}>
            <PencilLine size={15} /> Edit activity
          </Link>
        </div>
      </article>
    );
  }
  if (material.kind === "image") {
    return (
      <ImageMaterialBlock
        material={material}
        position={position}
        editHref={editHref}
        editRouteState={editRouteState}
      />
    );
  }
  return (
    <FileMaterialBlock
      material={material}
      position={position}
      editHref={editHref}
      editRouteState={editRouteState}
    />
  );
}

function ExternalMaterialBlock({
  activityId,
  material,
  position,
}: {
  activityId: string;
  material: Extract<ActivityMaterial, { kind: "link" }>;
  position: number;
}) {
  const embed = material.resourceType === "video" ? trustedVideoEmbed(material.url) : null;
  return (
    <article className="material-block">
      <div className="material-block-body">
        <MaterialKind position={position}>
          {material.resourceType} · {material.provider}
        </MaterialKind>
        <h3>{material.title}</h3>
        {material.durationMinutes ? <p>{material.durationMinutes} min</p> : null}
        {embed ? (
          <div className="video-frame">
            <iframe
              src={embed}
              title={material.title}
              allow="accelerometer; autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
            />
          </div>
        ) : material.resourceType === "video" ? (
          <p className="material-fallback">
            This provider is not embedded. Open the original video securely instead.
          </p>
        ) : null}
        <a
          href={material.url}
          target="_blank"
          rel="noopener noreferrer"
          data-memokite-activity-id={activityId}
          data-memokite-side-panel="lesson-workspace"
        >
          {`Open ${material.resourceType}`} <ExternalLink size={15} />
        </a>
      </div>
    </article>
  );
}

function ImageMaterialBlock({
  material,
  position,
  editHref,
  editRouteState,
}: {
  material: Extract<ActivityMaterial, { kind: "image" }>;
  position: number;
  editHref: string;
  editRouteState: ActivityEditRouteState;
}) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <figure className="material-block material-unavailable">
        <div className="material-block-body">
          <ImageOff size={22} />
          <MaterialKind position={position}>Image unavailable</MaterialKind>
          <h3>{material.file.originalFilename}</h3>
          <p>The image could not be loaded. Other materials are still available.</p>
          <Link to={editHref} state={editRouteState}>
            <PencilLine size={15} /> Edit activity
          </Link>
        </div>
      </figure>
    );
  }
  return (
    <figure className="material-block image-material">
      <div className="material-block-body">
        <MaterialKind position={position}>Image</MaterialKind>
        <a
          href={material.file.contentUrl ?? undefined}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Open ${material.file.originalFilename} full size`}
        >
          <img
            src={material.file.contentUrl ?? undefined}
            alt={material.decorative ? "" : material.altText}
            onError={() => setFailed(true)}
          />
        </a>
        <figcaption>
          {material.decorative ? material.file.originalFilename : material.altText}
        </figcaption>
      </div>
    </figure>
  );
}

function FileMaterialBlock({
  material,
  position,
  editHref,
  editRouteState,
}: {
  material: Extract<ActivityMaterial, { kind: "file" }>;
  position: number;
  editHref: string;
  editRouteState: ActivityEditRouteState;
}) {
  const [failed, setFailed] = useState(false);
  const [checking, setChecking] = useState(false);
  if (failed) {
    return (
      <article className="material-block material-unavailable" role="status">
        <div className="material-block-body">
          <ImageOff size={22} />
          <MaterialKind position={position}>File unavailable</MaterialKind>
          <h3>{material.file.originalFilename}</h3>
          <p>This file is missing or unavailable. Other materials still work.</p>
          <Link to={editHref} state={editRouteState}>
            <PencilLine size={15} /> Edit activity
          </Link>
        </div>
      </article>
    );
  }
  return (
    <article className="material-block file-material">
      <div className="material-block-body">
        <MaterialKind position={position}>Download</MaterialKind>
        <h3>{material.file.originalFilename}</h3>
        <p>
          {fileTypeLabel(material.file.contentType)} · {formatBytes(material.file.size)}
        </p>
        <a
          href={material.file.contentUrl ?? undefined}
          aria-disabled={checking}
          onClick={(event) => {
            event.preventDefault();
            if (checking || !material.file.contentUrl) return;
            setChecking(true);
            void checkUploadAvailability(material.fileId).then(
              () => {
                const download = document.createElement("a");
                download.href = material.file.contentUrl ?? "";
                download.click();
                setChecking(false);
              },
              () => {
                setChecking(false);
                setFailed(true);
              },
            );
          }}
        >
          <FileDown size={16} /> {checking ? "Checking file…" : "Download file"}
        </a>
      </div>
    </article>
  );
}

function MaterialKind({ position, children }: { position: number; children: React.ReactNode }) {
  return (
    <div className="material-kind-row">
      <span className="material-position">{position.toString().padStart(2, "0")}</span>
      <span className="material-kind">{children}</span>
    </div>
  );
}

export function NotesPanel({
  activity,
  compact = false,
}: {
  activity: ActivityDetail;
  compact?: boolean;
}) {
  const queryClient = useQueryClient();
  const flashcardOptions = useQuery({
    queryKey: flashcardOptionsKey(activity.id),
    queryFn: () => loadFlashcardOptions(activity.id),
  });
  const serverVersion = activity.note?.version ?? null;
  const serverNoteSnapshot = JSON.stringify({
    document: activity.note?.document ?? emptyDocument,
    version: serverVersion,
  });
  const [dirty, setDirty] = useState(false);
  const [draftConflict, setDraftConflict] = useState(false);
  const [message, setMessage] = useState("Autosave ready.");
  const [saveError, setSaveError] = useState("");
  const synchronizing = useRef(false);
  const allowTabEscape = useRef(false);
  const autosaveTimer = useRef<number | null>(null);
  const baseVersionRef = useRef<number | null>(serverVersion);
  const dirtyRef = useRef(false);
  const draftConflictRef = useRef(false);
  const autosaveBlockedRef = useRef(false);
  const revisionRef = useRef(0);
  const lastChangeAtRef = useRef(0);
  const persistedDocumentRef = useRef(JSON.stringify(activity.note?.document ?? emptyDocument));
  const observedServerNoteRef = useRef(serverNoteSnapshot);
  const inFlightRef = useRef<NoteSaveAttempt | null>(null);
  const handleDocumentChangeRef = useRef<(currentEditor: Editor) => void>(() => undefined);
  const flushRef = useRef<() => void>(() => undefined);
  const flashcardOpenerRef = useRef<HTMLElement | null>(null);
  const createFlashcardButtonRef = useRef<HTMLButtonElement>(null);
  const notesTabRef = useRef<HTMLButtonElement>(null);
  const flashcardsTabRef = useRef<HTMLButtonElement>(null);
  const [flashcardQuestion, setFlashcardQuestion] = useState<string | null>(null);
  const [flashcardMenu, setFlashcardMenu] = useState<{
    left: number;
    selection: NoteTextSelection;
    top: number;
  } | null>(null);
  const [clipboardError, setClipboardError] = useState("");
  const [activePanel, setActivePanel] = useState<"notes" | "flashcards">("notes");

  const clearAutosaveTimer = () => {
    if (autosaveTimer.current === null) return;
    window.clearTimeout(autosaveTimer.current);
    autosaveTimer.current = null;
  };

  const updateDirty = (nextDirty: boolean) => {
    dirtyRef.current = nextDirty;
    setDirty(nextDirty);
  };

  const updateDraftConflict = (nextConflict: boolean) => {
    draftConflictRef.current = nextConflict;
    setDraftConflict(nextConflict);
  };

  const scheduleAutosave = (delay = noteAutosaveDebounceMs) => {
    clearAutosaveTimer();
    if (!dirtyRef.current || autosaveBlockedRef.current || draftConflictRef.current) {
      return;
    }
    autosaveTimer.current = window.setTimeout(() => {
      autosaveTimer.current = null;
      flushRef.current();
    }, delay);
  };

  const editor = useEditor({
    injectCSS: false,
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        code: false,
        codeBlock: false,
        horizontalRule: false,
        strike: false,
        link: false,
      }),
      LinkExtension.configure({
        openOnClick: false,
        autolink: false,
        linkOnPaste: false,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
    ],
    content: (activity.note?.document ?? emptyDocument) as JSONContent,
    editorProps: {
      attributes: {
        "aria-label": "Personal learning notes",
        class: "notes-editor-content",
      },
      handleKeyDown: (view, event) => {
        if (event.key === "Escape") {
          allowTabEscape.current = true;
          return true;
        }
        if (event.key !== "Tab") {
          allowTabEscape.current = false;
          return false;
        }
        const domSelection = view.dom.ownerDocument.getSelection();
        if (
          domSelection?.anchorNode &&
          domSelection.focusNode &&
          view.dom.contains(domSelection.anchorNode) &&
          view.dom.contains(domSelection.focusNode)
        ) {
          try {
            const anchor = view.posAtDOM(domSelection.anchorNode, domSelection.anchorOffset);
            const head = view.posAtDOM(domSelection.focusNode, domSelection.focusOffset);
            if (anchor !== view.state.selection.anchor || head !== view.state.selection.head) {
              view.dispatch(
                view.state.tr.setSelection(TextSelection.create(view.state.doc, anchor, head)),
              );
            }
          } catch {
            // ProseMirror remains the source of truth if a transient DOM selection cannot map.
          }
        }
        if (allowTabEscape.current && !event.shiftKey) {
          allowTabEscape.current = false;
          return false;
        }
        allowTabEscape.current = false;
        const { $from } = view.state.selection;
        let insideListItem = false;
        for (let depth = $from.depth; depth > 0; depth -= 1) {
          if ($from.node(depth).type.name === "listItem") {
            insideListItem = true;
            break;
          }
        }
        if (insideListItem) return false;
        if (event.altKey || event.ctrlKey || event.metaKey) return false;
        if (event.shiftKey) {
          if (view.state.selection.empty) {
            let textblockDepth = $from.depth;
            while (textblockDepth > 0 && !$from.node(textblockDepth).isTextblock) {
              textblockDepth -= 1;
            }
            if (textblockDepth > 0) {
              const textblock = $from.node(textblockDepth);
              if (textblock.firstChild?.isText && textblock.firstChild.text?.startsWith("\t")) {
                const textblockStart = $from.start(textblockDepth);
                const transaction = view.state.tr.delete(textblockStart, textblockStart + 1);
                transaction.setSelection(
                  view.state.selection.map(transaction.doc, transaction.mapping),
                );
                view.dispatch(transaction.scrollIntoView());
              }
            }
          }
          view.focus();
          return true;
        }
        view.dispatch(view.state.tr.insertText("\t").scrollIntoView());
        return true;
      },
    },
    onUpdate: ({ editor: currentEditor }) => {
      if (synchronizing.current) return;
      handleDocumentChangeRef.current(currentEditor);
    },
    onBlur: () => {
      allowTabEscape.current = false;
    },
  });
  const formatting = useEditorState({
    editor,
    selector: ({ editor: current }) => ({
      heading: current?.isActive("heading", { level: 2 }) ?? false,
      subheading: current?.isActive("heading", { level: 3 }) ?? false,
      bold: current?.isActive("bold") ?? false,
      italic: current?.isActive("italic") ?? false,
      bulletedList: current?.isActive("bulletList") ?? false,
      selectionText: current ? selectedNoteText(current) : "",
    }),
  });
  const mutation = useMutation({
    mutationFn: (attempt: NoteSaveAttempt) =>
      saveActivityNote(activity.id, {
        version: attempt.version,
        document: attempt.document,
      }),
    onSuccess: async (result, attempt) => {
      clearAutosaveTimer();
      const nextVersion = result.note?.version ?? null;
      baseVersionRef.current = nextVersion;
      persistedDocumentRef.current = JSON.stringify(attempt.document);
      autosaveBlockedRef.current = false;
      updateDraftConflict(false);
      setSaveError("");
      if (revisionRef.current === attempt.revision) {
        updateDirty(false);
        setMessage(result.note ? "Notes saved." : "Saved notes cleared.");
      } else {
        updateDirty(true);
        setMessage("Unsaved changes.");
      }
      await queryClient.invalidateQueries({ queryKey: activityDetailKey(activity.id) });
    },
    onError: (error) => {
      clearAutosaveTimer();
      updateDirty(true);
      autosaveBlockedRef.current = true;
      setMessage("");
      if (error instanceof ApiError && error.problem?.code === "ACTIVITY_NOTE_VERSION_CONFLICT") {
        updateDraftConflict(true);
        setSaveError("");
        void queryClient.invalidateQueries({ queryKey: activityDetailKey(activity.id) });
        return;
      }
      setSaveError("Notes were not saved. Your draft is still here; try again.");
    },
    onSettled: (_result, _error, attempt) => {
      if (inFlightRef.current?.revision === attempt.revision) {
        inFlightRef.current = null;
      }
      if (dirtyRef.current && !autosaveBlockedRef.current && !draftConflictRef.current) {
        const elapsed = Date.now() - lastChangeAtRef.current;
        scheduleAutosave(Math.max(0, noteAutosaveDebounceMs - elapsed));
      }
    },
  });

  handleDocumentChangeRef.current = (currentEditor) => {
    revisionRef.current += 1;
    lastChangeAtRef.current = Date.now();
    setSaveError("");

    if (!draftConflictRef.current) {
      autosaveBlockedRef.current = false;
    }

    const matchesPersistedDocument =
      JSON.stringify(currentEditor.getJSON()) === persistedDocumentRef.current;
    if (!inFlightRef.current && matchesPersistedDocument) {
      clearAutosaveTimer();
      updateDirty(false);
      setMessage("Notes saved.");
      return;
    }

    updateDirty(true);
    setMessage("Unsaved changes.");
    scheduleAutosave();
  };

  flushRef.current = () => {
    clearAutosaveTimer();
    if (
      !dirtyRef.current ||
      inFlightRef.current ||
      draftConflictRef.current ||
      autosaveBlockedRef.current ||
      !editor
    ) {
      return;
    }
    const parsed = richTextDocumentSchema.safeParse(editor.getJSON());
    if (!parsed.success) {
      setSaveError("These notes contain unsupported formatting. Remove it and try again.");
      setMessage("");
      autosaveBlockedRef.current = true;
      return;
    }
    const attempt: NoteSaveAttempt = {
      document: parsed.data,
      revision: revisionRef.current,
      version: baseVersionRef.current,
    };
    inFlightRef.current = attempt;
    autosaveBlockedRef.current = false;
    setSaveError("");
    setMessage("Saving…");
    mutation.mutate(attempt);
  };

  useEffect(() => {
    if (!editor || observedServerNoteRef.current === serverNoteSnapshot) return;
    observedServerNoteRef.current = serverNoteSnapshot;
    if (serverVersion === baseVersionRef.current) return;
    if (dirtyRef.current) {
      clearAutosaveTimer();
      autosaveBlockedRef.current = true;
      updateDraftConflict(true);
      setMessage("");
      return;
    }
    synchronizing.current = true;
    editor.commands.setContent((activity.note?.document ?? emptyDocument) as JSONContent, {
      emitUpdate: false,
    });
    synchronizing.current = false;
    persistedDocumentRef.current = JSON.stringify(activity.note?.document ?? emptyDocument);
    baseVersionRef.current = serverVersion;
    updateDirty(false);
    updateDraftConflict(false);
    autosaveBlockedRef.current = false;
    setSaveError("");
    setMessage("");
  }, [activity.note?.document, editor, serverNoteSnapshot, serverVersion]);

  useEffect(
    () => () => {
      if (autosaveTimer.current !== null) {
        window.clearTimeout(autosaveTimer.current);
        autosaveTimer.current = null;
      }
    },
    [activity.id],
  );

  if (!editor) return <section className="learning-panel notes-panel" aria-busy="true" />;
  const toggleNoteMark = (mark: "bold" | "italic") => {
    if (editor.state.selection.empty) {
      const markType = editor.schema.marks[mark];
      if (!markType) return;
      const activeMarks = editor.state.storedMarks ?? editor.state.selection.$from.marks();
      const markIsActive = activeMarks.some((activeMark) => activeMark.type === markType);
      const nextMarks = markIsActive
        ? activeMarks.filter((activeMark) => activeMark.type !== markType)
        : [...activeMarks, markType.create()];
      editor.view.dispatch(editor.state.tr.setStoredMarks(nextMarks));
      editor.view.focus();
      return;
    }
    editor.chain().focus().toggleMark(mark).run();
  };
  const loadLatest = async () => {
    if (
      dirty &&
      !window.confirm(
        "Replace your local draft with the latest saved note? Your draft will be lost.",
      )
    ) {
      return;
    }
    const latest = await queryClient
      .fetchQuery({
        queryKey: activityDetailKey(activity.id),
        queryFn: () => loadActivityDetail(activity.id),
      })
      .catch(() => null);
    if (!latest) return;
    synchronizing.current = true;
    editor.commands.setContent((latest.note?.document ?? emptyDocument) as JSONContent, {
      emitUpdate: false,
    });
    synchronizing.current = false;
    clearAutosaveTimer();
    const latestVersion = latest.note?.version ?? null;
    persistedDocumentRef.current = JSON.stringify(latest.note?.document ?? emptyDocument);
    observedServerNoteRef.current = JSON.stringify({
      document: latest.note?.document ?? emptyDocument,
      version: latestVersion,
    });
    baseVersionRef.current = latestVersion;
    updateDirty(false);
    updateDraftConflict(false);
    autosaveBlockedRef.current = false;
    inFlightRef.current = null;
    mutation.reset();
    setSaveError("");
    setMessage("Latest saved note loaded.");
    editor.commands.focus();
  };
  const openFlashcard = (question: string, opener: HTMLElement | null) => {
    flashcardOpenerRef.current = opener;
    setFlashcardMenu(null);
    setClipboardError("");
    setFlashcardQuestion(question);
  };
  const closeFlashcard = () => {
    const opener = flashcardOpenerRef.current;
    setFlashcardQuestion(null);
    window.requestAnimationFrame(() => opener?.focus());
  };
  const restoreNoteSelection = (selection: NoteTextSelection) => {
    editor.commands.setTextSelection({ from: selection.from, to: selection.to });
    editor.commands.focus();
  };
  const closeFlashcardMenu = (restoreFocus: boolean) => {
    const selection = flashcardMenu?.selection;
    setFlashcardMenu(null);
    if (restoreFocus && selection) {
      window.requestAnimationFrame(() => restoreNoteSelection(selection));
    }
  };
  const writeSelectionToClipboard = async (selection: NoteTextSelection) => {
    if (!navigator.clipboard?.writeText) {
      throw new Error("Clipboard writing is unavailable.");
    }
    await navigator.clipboard.writeText(selection.text);
  };
  const handleClipboardFailure = (selection: NoteTextSelection) => {
    setClipboardError("Could not access the clipboard. Your notes were not changed.");
    setFlashcardMenu(null);
    window.requestAnimationFrame(() => restoreNoteSelection(selection));
  };
  const copyNoteSelection = async (selection: NoteTextSelection) => {
    try {
      await writeSelectionToClipboard(selection);
      setClipboardError("");
      setFlashcardMenu(null);
      window.requestAnimationFrame(() => restoreNoteSelection(selection));
    } catch {
      handleClipboardFailure(selection);
    }
  };
  const cutNoteSelection = async (selection: NoteTextSelection) => {
    try {
      await writeSelectionToClipboard(selection);
      setClipboardError("");
      setFlashcardMenu(null);
      editor.view.dispatch(editor.state.tr.delete(selection.from, selection.to).scrollIntoView());
      editor.commands.focus();
    } catch {
      handleClipboardFailure(selection);
    }
  };
  const pasteOverNoteSelection = async (selection: NoteTextSelection) => {
    try {
      if (!navigator.clipboard?.readText) {
        throw new Error("Clipboard reading is unavailable.");
      }
      const clipboardText = await navigator.clipboard.readText();
      setClipboardError("");
      setFlashcardMenu(null);
      editor.commands.setTextSelection({ from: selection.from, to: selection.to });
      editor.view.pasteText(clipboardText);
      editor.commands.focus();
    } catch {
      handleClipboardFailure(selection);
    }
  };
  const createFlashcardButton = (
    <button
      ref={createFlashcardButtonRef}
      type="button"
      className="soft-button"
      onClick={() => openFlashcard("", createFlashcardButtonRef.current)}
    >
      <Plus size={15} aria-hidden="true" /> Create flash card
    </button>
  );
  return (
    <section
      className={`learning-panel notes-panel${compact ? " notes-panel--compact" : ""}`}
      aria-labelledby="activity-notes-heading"
    >
      {compact ? (
        <div className="notes-heading-row notes-heading-row--compact">
          <span id="activity-notes-heading" className="section-kicker">
            Personal Notes
          </span>
          <div className="flashcard-create-summary">{createFlashcardButton}</div>
        </div>
      ) : (
        <div className="notes-heading-row">
          <h2 id="activity-notes-heading">Personal notes</h2>
          <div className="flashcard-create-summary">{createFlashcardButton}</div>
        </div>
      )}
      <div
        className="lesson-workspace-tabs"
        role="tablist"
        aria-label="Lesson workspace"
        onKeyDown={(event) => {
          if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
          event.preventDefault();
          const nextPanel =
            event.key === "Home"
              ? "notes"
              : event.key === "End"
                ? "flashcards"
                : activePanel === "notes"
                  ? "flashcards"
                  : "notes";
          setActivePanel(nextPanel);
          window.requestAnimationFrame(() =>
            (nextPanel === "notes" ? notesTabRef.current : flashcardsTabRef.current)?.focus(),
          );
        }}
      >
        <button
          id="lesson-notes-tab"
          ref={notesTabRef}
          type="button"
          role="tab"
          tabIndex={activePanel === "notes" ? 0 : -1}
          aria-selected={activePanel === "notes"}
          aria-controls="lesson-notes-panel"
          onClick={() => setActivePanel("notes")}
        >
          Notes
        </button>
        <button
          id="lesson-flashcards-tab"
          ref={flashcardsTabRef}
          type="button"
          role="tab"
          tabIndex={activePanel === "flashcards" ? 0 : -1}
          aria-label={
            flashcardOptions.data
              ? `Flashcards (${flashcardOptions.data.flashcardCount.toString()})`
              : "Flashcards"
          }
          aria-selected={activePanel === "flashcards"}
          aria-controls="lesson-flashcards-panel"
          onClick={() => setActivePanel("flashcards")}
        >
          Flashcards
          {flashcardOptions.data ? (
            <span aria-hidden="true">{flashcardOptions.data.flashcardCount}</span>
          ) : null}
        </button>
      </div>
      <div
        id="lesson-notes-panel"
        className="lesson-workspace-panel notes-workspace-panel"
        role="tabpanel"
        aria-labelledby="lesson-notes-tab"
        hidden={activePanel !== "notes"}
      >
        <div className="notes-toolbar" role="toolbar" aria-label="Note formatting">
          <button
            type="button"
            aria-label={compact ? "Heading" : undefined}
            aria-pressed={formatting?.heading ?? false}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          >
            {compact ? <Heading1 size={16} aria-hidden="true" /> : "Heading"}
          </button>
          <button
            type="button"
            aria-label={compact ? "Subheading" : undefined}
            aria-pressed={formatting?.subheading ?? false}
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          >
            {compact ? <Heading2 size={16} aria-hidden="true" /> : "Subheading"}
          </button>
          <button
            type="button"
            aria-label={compact ? "Bold" : undefined}
            aria-pressed={formatting?.bold ?? false}
            onClick={() => toggleNoteMark("bold")}
          >
            {compact ? <Bold size={16} aria-hidden="true" /> : "Bold"}
          </button>
          <button
            type="button"
            aria-label={compact ? "Italic" : undefined}
            aria-pressed={formatting?.italic ?? false}
            onClick={() => toggleNoteMark("italic")}
          >
            {compact ? <Italic size={16} aria-hidden="true" /> : "Italic"}
          </button>
          <button
            type="button"
            aria-label={compact ? "Bulleted list" : undefined}
            aria-pressed={formatting?.bulletedList ?? false}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            {compact ? <List size={16} aria-hidden="true" /> : "Bulleted list"}
          </button>
          <button
            type="button"
            aria-label={compact ? "Create flash card from selection" : undefined}
            disabled={!formatting?.selectionText}
            onClick={(event) => openFlashcard(formatting?.selectionText ?? "", event.currentTarget)}
          >
            {compact ? (
              <BadgePlus size={16} aria-hidden="true" />
            ) : (
              "Create flash card from selection"
            )}
          </button>
        </div>
        <EditorContent
          className="notes-editor-shell"
          editor={editor}
          onContextMenu={(event) => {
            const selection = selectedNoteSelection(editor);
            if (!selection) return;
            event.preventDefault();
            setClipboardError("");
            setFlashcardMenu({ left: event.clientX, top: event.clientY, selection });
          }}
        />
        {flashcardMenu ? (
          <NoteFlashcardMenu
            left={flashcardMenu.left}
            top={flashcardMenu.top}
            onCopy={() => void copyNoteSelection(flashcardMenu.selection)}
            onCreate={() => openFlashcard(flashcardMenu.selection.text, editor.view.dom)}
            onCut={() => void cutNoteSelection(flashcardMenu.selection)}
            onClose={closeFlashcardMenu}
            onPaste={() => void pasteOverNoteSelection(flashcardMenu.selection)}
          />
        ) : null}
        {!compact || saveError || draftConflict ? (
          <p
            className={saveError || draftConflict ? "mutation-error" : "mutation-status"}
            role={saveError || draftConflict ? "alert" : "status"}
            aria-live="polite"
            aria-atomic="true"
          >
            {draftConflict
              ? "These notes changed elsewhere. Your draft is still here and was not saved."
              : saveError || message}
          </p>
        ) : null}
        {clipboardError ? (
          <p className="mutation-error" role="alert" aria-live="assertive">
            {clipboardError}
          </p>
        ) : null}
        {draftConflict ? (
          <button type="button" className="text-button" onClick={() => void loadLatest()}>
            Load latest saved note
          </button>
        ) : null}
      </div>
      <div
        id="lesson-flashcards-panel"
        className="lesson-workspace-panel flashcards-workspace-panel"
        role="tabpanel"
        aria-labelledby="lesson-flashcards-tab"
        hidden={activePanel !== "flashcards"}
      >
        <LessonFlashcardsPanel activityId={activity.id} active={activePanel === "flashcards"} />
      </div>
      {flashcardQuestion !== null ? (
        <FlashcardDialog
          activityId={activity.id}
          initialQuestion={flashcardQuestion}
          collections={flashcardOptions.data?.collections ?? []}
          onCollectionCreated={(collection) => {
            queryClient.setQueryData<FlashcardOptionsResponse>(
              flashcardOptionsKey(activity.id),
              (current) => ({
                collections: [...(current?.collections ?? []), collection],
                flashcardCount: current?.flashcardCount ?? 0,
              }),
            );
          }}
          onSaved={(result) => {
            queryClient.setQueryData<FlashcardOptionsResponse>(
              flashcardOptionsKey(activity.id),
              (current) => ({
                collections: current?.collections ?? flashcardOptions.data?.collections ?? [],
                flashcardCount: result.flashcardCount,
              }),
            );
            queryClient.setQueryData<LessonFlashcardsResponse>(
              lessonFlashcardsKey(activity.id),
              (current) =>
                current
                  ? { flashcards: mergeFlashcards(current.flashcards, result.flashcards) }
                  : current,
            );
            void queryClient.invalidateQueries({ queryKey: lessonFlashcardsKey(activity.id) });
            closeFlashcard();
          }}
          onClose={closeFlashcard}
        />
      ) : null}
    </section>
  );
}

function selectedNoteText(editor: Editor): string {
  return selectedNoteSelection(editor)?.text ?? "";
}

function selectedNoteSelection(editor: Editor): NoteTextSelection | null {
  const { empty, from, to } = editor.state.selection;
  if (empty) return null;
  const text = editor.state.doc.textBetween(from, to, "\n");
  return text.trim() ? { from, text, to } : null;
}

function mergeFlashcards<T extends { id: string }>(current: T[], added: T[]): T[] {
  const ids = new Set(current.map((card) => card.id));
  return [...current, ...added.filter((card) => !ids.has(card.id))];
}

function NoteFlashcardMenu({
  left,
  top,
  onCopy,
  onCreate,
  onCut,
  onClose,
  onPaste,
}: {
  left: number;
  top: number;
  onCopy: () => void;
  onCreate: () => void;
  onCut: () => void;
  onClose: (restoreFocus: boolean) => void;
  onPaste: () => void;
}) {
  const menuRef = useRef<HTMLDivElement>(null);
  const firstItemRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    firstItemRef.current?.focus();
    const dismiss = (event: PointerEvent) => {
      if (event.target instanceof Node && menuRef.current?.contains(event.target)) return;
      onCloseRef.current(false);
    };
    const dismissOnBlur = () => onCloseRef.current(false);
    window.addEventListener("pointerdown", dismiss);
    window.addEventListener("blur", dismissOnBlur);
    return () => {
      window.removeEventListener("pointerdown", dismiss);
      window.removeEventListener("blur", dismissOnBlur);
    };
  }, []);
  const viewportMargin = 4;
  const menuWidth = Math.min(150, Math.max(0, window.innerWidth - viewportMargin * 2));
  const menuHeight = Math.min(122, Math.max(0, window.innerHeight - viewportMargin * 2));
  const containedLeft = Math.max(
    viewportMargin,
    Math.min(left, window.innerWidth - menuWidth - viewportMargin),
  );
  const containedTop = Math.max(
    viewportMargin,
    Math.min(top, window.innerHeight - menuHeight - viewportMargin),
  );
  return (
    <div
      ref={menuRef}
      className="note-flashcard-menu"
      role="menu"
      aria-label="Selected note actions"
      style={{ left: containedLeft, top: containedTop, width: menuWidth }}
      onKeyDown={(event) => {
        const items = Array.from(
          event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitem"]'),
        );
        const activeIndex = items.indexOf(document.activeElement as HTMLButtonElement);
        let nextIndex: number | null = null;
        if (event.key === "ArrowDown") {
          nextIndex = (activeIndex + 1) % items.length;
        } else if (event.key === "ArrowUp") {
          nextIndex = (activeIndex - 1 + items.length) % items.length;
        } else if (event.key === "Home") {
          nextIndex = 0;
        } else if (event.key === "End") {
          nextIndex = items.length - 1;
        } else if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          items[activeIndex]?.click();
          return;
        } else if (event.key === "Escape") {
          event.preventDefault();
          onClose(true);
          return;
        }
        if (nextIndex !== null) {
          event.preventDefault();
          items[nextIndex]?.focus();
        }
      }}
    >
      <button ref={firstItemRef} type="button" role="menuitem" onClick={onCut}>
        Cut
      </button>
      <button type="button" role="menuitem" onClick={onCopy}>
        Copy
      </button>
      <button type="button" role="menuitem" onClick={onPaste}>
        Paste
      </button>
      <button
        type="button"
        role="menuitem"
        className="note-flashcard-menu-create"
        onClick={onCreate}
      >
        Create flash card
      </button>
    </div>
  );
}

function ActivitySessionBanner({
  activity,
  returnTo,
}: {
  activity: ActivityDetail;
  returnTo: string;
}) {
  const queryClient = useQueryClient();
  const today = useQuery({
    queryKey: todayQueryRoot,
    queryFn: loadToday,
    staleTime: 60_000,
  });
  const [historyOpen, setHistoryOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [completionError, setCompletionError] = useState("");
  const [retryCompletion, setRetryCompletion] = useState<SessionCommand | null>(null);
  const [reflectionCompletionId, setReflectionCompletionId] = useState<string | null>(null);
  const [deleteSession, setDeleteSession] = useState<ActivitySession | null>(null);
  const [undoCommand, setUndoCommand] = useState<SessionCommand | null>(null);
  const [undoError, setUndoError] = useState("");
  const reflectionSourceSessionId = useRef<string | null>(null);
  const deleteTriggerRef = useRef<HTMLButtonElement | null>(null);
  const undoTriggerRef = useRef<HTMLButtonElement | null>(null);
  const deleteInFlightRef = useRef(false);
  const completionInFlightRef = useRef(false);
  const undoInFlightRef = useRef(false);
  const focusTargetRef = useRef<
    { action: "studied" } | { sessionId: string; action: "complete" | "undo" } | null
  >(null);
  const todayDate = today.data?.date ?? null;
  const plannedSessions = selectPlannedActivitySessions(activity.sessions);
  const completedSessions = activity.sessions
    .filter((session) => session.status === "completed")
    .toSorted(compareActivitySessions);

  const deleteMutation = useMutation({
    mutationFn: (session: ActivitySession) =>
      cancelActivitySession(session.id, {
        version: session.version,
        idempotencyKey: createUuid(),
      }),
    onSuccess: async () => {
      deleteInFlightRef.current = false;
      setDeleteSession(null);
      setMessage("Session deleted from your schedule. Its history is still kept.");
      await invalidateLearning(queryClient, activity.id);
    },
    onError: () => {
      deleteInFlightRef.current = false;
    },
  });
  const completeMutation = useMutation({
    mutationFn: (command: SessionCommand) =>
      completeActivitySession(command.session.id, {
        version: command.session.version,
        idempotencyKey: command.idempotencyKey,
      }),
    onSuccess: async (result) => {
      setCompletionError("");
      setRetryCompletion(null);
      setMessage(result.replayed ? "Completion was already recorded." : "Completion recorded.");
      reflectionSourceSessionId.current = result.session.id;
      focusTargetRef.current = { action: "studied" };
      setHistoryOpen(false);
      await invalidateLearning(queryClient, activity.id);
    },
    onError: (error, command) => {
      const changed = isSessionConflict(error);
      setCompletionError(
        changed
          ? "This session changed in another view. Refresh the activity before changing it again."
          : "The completion was not recorded. Nothing changed; try again.",
      );
      setRetryCompletion(changed ? null : command);
      if (changed) void queryClient.invalidateQueries({ queryKey: activityDetailKey(activity.id) });
      focusTargetRef.current = { sessionId: command.session.id, action: "complete" };
    },
    onSettled: () => {
      completionInFlightRef.current = false;
    },
  });
  const undoMutation = useMutation({
    mutationFn: (command: SessionCommand) =>
      undoActivitySession(command.session.id, {
        version: command.session.version,
        idempotencyKey: command.idempotencyKey,
      }),
    onSuccess: async (result, command) => {
      setReflectionCompletionId(null);
      setUndoError("");
      setMessage(result.replayed ? "Completion was already undone." : "Completion undone.");
      focusTargetRef.current = { sessionId: command.session.id, action: "complete" };
      await invalidateLearning(queryClient, activity.id);
      setUndoCommand(null);
    },
    onError: (error, command) => {
      const changed = isSessionConflict(error);
      if (changed) {
        setCompletionError(
          "This session changed in another view. The activity has been refreshed.",
        );
        setRetryCompletion(null);
        focusTargetRef.current = { sessionId: command.session.id, action: "complete" };
        void queryClient
          .invalidateQueries({ queryKey: activityDetailKey(activity.id) })
          .then(() => setUndoCommand(null));
      } else {
        setUndoError("The completion was not undone. Nothing changed; try again.");
      }
    },
    onSettled: () => {
      undoInFlightRef.current = false;
    },
  });
  useEffect(() => {
    const target = focusTargetRef.current;
    if (!target || completeMutation.isPending || undoMutation.isPending) return;
    const selector =
      target.action === "studied"
        ? '.activity-studied-cell[data-session-action="studied"]'
        : `[data-banner-session-id="${target.sessionId}"] [data-session-action="${target.action}"]`;
    const timer = window.setTimeout(() => {
      const control = document.querySelector<HTMLButtonElement>(selector);
      if (control && !control.disabled) {
        control.focus({ preventScroll: true });
        focusTargetRef.current = null;
      } else {
        const fallback = document.querySelector<HTMLButtonElement>(
          '.activity-session-banner [data-session-action="complete"], .activity-studied-cell[aria-expanded]',
        );
        fallback?.focus({ preventScroll: true });
        focusTargetRef.current = null;
      }
    }, 0);
    return () => window.clearTimeout(timer);
  }, [activity.sessions, completeMutation.isPending, undoMutation.isPending]);
  const submitCompletion = (command: SessionCommand) => {
    if (completionInFlightRef.current) return;
    completionInFlightRef.current = true;
    setCompletionError("");
    setMessage("");
    completeMutation.mutate(command);
  };
  const requestCompletion = (session: ActivitySession) => {
    submitCompletion({
      session,
      idempotencyKey: createUuid(),
    });
  };
  const requestUndo = (session: ActivitySession, trigger: HTMLButtonElement) => {
    undoMutation.reset();
    setUndoError("");
    setMessage("");
    undoTriggerRef.current = trigger;
    setUndoCommand({
      session,
      idempotencyKey: createUuid(),
    });
  };
  const confirmUndo = () => {
    if (!undoCommand || undoInFlightRef.current) return;
    undoInFlightRef.current = true;
    undoMutation.mutate(undoCommand);
  };
  const dismissUndoDialog = () => {
    if (undoMutation.isPending) return;
    setUndoCommand(null);
    setUndoError("");
    window.setTimeout(() => undoTriggerRef.current?.focus(), 0);
  };
  const dismissDeleteDialog = () => {
    if (deleteMutation.isPending) return;
    setDeleteSession(null);
    window.setTimeout(() => deleteTriggerRef.current?.focus(), 0);
  };
  const dismissReflectionDialog = () => {
    const sourceSessionId = reflectionSourceSessionId.current;
    setReflectionCompletionId(null);
    window.setTimeout(() => {
      if (!sourceSessionId) return;
      document
        .querySelector<HTMLButtonElement>(`[data-reflection-session-id="${sourceSessionId}"]`)
        ?.focus();
    }, 0);
  };
  const openReflection = (session: ActivitySession) => {
    const completion = activity.completionHistory.find(
      (candidate) => candidate.sessionId === session.id && candidate.reversedAt === null,
    );
    if (!completion) return;
    reflectionSourceSessionId.current = session.id;
    setReflectionCompletionId(completion.id);
  };
  return (
    <section className="activity-session-banner" aria-label="Activity details and schedule">
      <div className="activity-detail-meta" aria-label="Activity details">
        <span className="activity-detail-meta-label">Activity details</span>
        <div className="activity-detail-meta-values">
          <span>
            <Clock3 size={15} aria-hidden="true" /> {activity.estimatedMinutes} min
          </span>
          <span>Interest {activity.interest === null ? "Unrated" : `${activity.interest}/5`}</span>
          <span>
            Difficulty {activity.difficulty === null ? "Unrated" : `${activity.difficulty}/5`}
          </span>
        </div>
        {!activity.archivedAt ? (
          <Link
            className="activity-add-session"
            to={`/activities/${activity.id}/schedule`}
            state={createActivityReturnState(returnTo)}
          >
            <CalendarPlus size={15} aria-hidden="true" /> Add session
          </Link>
        ) : null}
      </div>
      <div className="activity-session-banner-body">
        {activity.completedSessionCount > 0 ? (
          <button
            type="button"
            className="activity-session-cell activity-studied-cell"
            data-session-action="studied"
            aria-expanded={historyOpen}
            aria-controls="activity-session-history"
            onClick={() => setHistoryOpen((current) => !current)}
          >
            <span className="activity-session-kicker">Studied</span>
            <strong>
              {activity.completedSessionCount}{" "}
              {activity.completedSessionCount === 1 ? "time" : "times"}
            </strong>
          </button>
        ) : (
          <div className="activity-session-cell activity-studied-cell">
            <span className="activity-session-kicker">Studied</span>
            <strong>0 times</strong>
          </div>
        )}
        {plannedSessions.displayed.map((session, index) => (
          <CompactPlannedSession
            key={session.id}
            activity={activity}
            session={session}
            next={index === 0}
            today={index === 0 && session.scheduledDate === todayDate}
            returnTo={returnTo}
            completing={completeMutation.isPending}
            onComplete={requestCompletion}
            onDelete={(trigger) => {
              deleteMutation.reset();
              setMessage("");
              deleteTriggerRef.current = trigger;
              setDeleteSession(session);
            }}
          />
        ))}
        {plannedSessions.remainingCount > 0 ? (
          <div className="activity-session-cell activity-session-overflow">
            <strong>
              {plannedSessions.remainingCount}{" "}
              {plannedSessions.remainingCount === 1 ? "session after" : "sessions after"}
            </strong>
          </div>
        ) : null}
      </div>
      {historyOpen && completedSessions.length > 0 ? (
        <section
          className="activity-session-history"
          id="activity-session-history"
          aria-label="Session history"
        >
          <ol className="session-list">
            {completedSessions.map((session) => (
              <li key={session.id} data-banner-session-id={session.id}>
                <div>
                  <strong>Session</strong>
                  <span>
                    {formatSessionDate(session)} · {activity.estimatedMinutes} min
                  </span>
                  <i className="session-status status-completed">completed</i>
                </div>
                <div className="session-actions">
                  <button
                    type="button"
                    data-reflection-trigger
                    data-reflection-session-id={session.id}
                    onClick={() => openReflection(session)}
                  >
                    Reflect &amp; plan sessions
                  </button>
                  <button
                    type="button"
                    data-session-action="undo"
                    onClick={(event) => requestUndo(session, event.currentTarget)}
                  >
                    <RotateCcw size={14} aria-hidden="true" /> Undo
                  </button>
                </div>
              </li>
            ))}
          </ol>
        </section>
      ) : null}
      {completionError ? (
        <div className="activity-session-error" role="alert">
          <span>{completionError}</span>
          {retryCompletion ? (
            <button
              type="button"
              className="soft-button"
              disabled={completeMutation.isPending}
              onClick={() => submitCompletion(retryCompletion)}
            >
              Try again
            </button>
          ) : (
            <button
              type="button"
              className="soft-button"
              onClick={() =>
                void queryClient.invalidateQueries({ queryKey: activityDetailKey(activity.id) })
              }
            >
              Refresh activity
            </button>
          )}
        </div>
      ) : null}
      {deleteSession ? (
        <DeleteSessionDialog
          session={deleteSession}
          pending={deleteMutation.isPending}
          failed={deleteMutation.isError}
          onDismiss={dismissDeleteDialog}
          onConfirm={() => {
            if (deleteInFlightRef.current) return;
            deleteInFlightRef.current = true;
            deleteMutation.mutate(deleteSession);
          }}
        />
      ) : null}
      {reflectionCompletionId ? (
        <CompletionReflectionDialog
          activity={activity}
          completionId={reflectionCompletionId}
          onClose={dismissReflectionDialog}
        />
      ) : null}
      {undoCommand ? (
        <UndoCompletionDialog
          activityTitle={activity.title}
          session={undoCommand.session}
          pending={undoMutation.isPending}
          error={undoError}
          onDismiss={dismissUndoDialog}
          onConfirm={confirmUndo}
        />
      ) : null}
      <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {message}
      </p>
    </section>
  );
}

type SessionCommand = {
  session: ActivitySession;
  idempotencyKey: string;
};

function CompactPlannedSession({
  activity,
  session,
  next,
  today,
  returnTo,
  completing,
  onComplete,
  onDelete,
}: {
  activity: ActivityDetail;
  session: ActivitySession;
  next: boolean;
  today: boolean;
  returnTo: string;
  completing: boolean;
  onComplete: (session: ActivitySession) => void;
  onDelete: (trigger: HTMLButtonElement) => void;
}): React.JSX.Element {
  const formattedDate = formatCompactSessionDate(session);
  return (
    <article
      className="activity-session-cell activity-planned-session"
      data-banner-session-id={session.id}
    >
      <span className="activity-session-kicker">
        {next ? "Next session" : "Session"}
        {today ? <i className="activity-today-badge">Today</i> : null}
      </span>
      <div className="activity-session-date">
        <strong>{formattedDate}</strong>
        {session.scheduledTime ? <span>{session.scheduledTime}</span> : null}
      </div>
      <div className="activity-session-icon-actions">
        {next ? (
          <button
            type="button"
            aria-label={`Complete session on ${formattedDate}`}
            data-session-action="complete"
            disabled={completing}
            onClick={() => onComplete(session)}
          >
            <Check size={17} aria-hidden="true" />
          </button>
        ) : null}
        <button
          type="button"
          aria-label={`Remove session on ${formattedDate}`}
          disabled={completing}
          onClick={(event) => onDelete(event.currentTarget)}
        >
          <Trash2 size={16} aria-hidden="true" />
        </button>
        <Link
          to={`/activities/${activity.id}/schedule?sessionId=${session.id}`}
          state={createActivityReturnState(returnTo)}
          aria-label={`Edit session on ${formattedDate}`}
        >
          <PencilLine size={16} aria-hidden="true" />
        </Link>
      </div>
    </article>
  );
}

function formatCompactSessionDate(session: ActivitySession): string {
  const [year, month, day] = session.scheduledDate.split("-").map(Number);
  if (!year || !month || !day) return session.scheduledDate;
  const date = new Date(Date.UTC(year, month - 1, day));
  const weekday = new Intl.DateTimeFormat("en", { weekday: "short", timeZone: "UTC" }).format(date);
  const monthName = new Intl.DateTimeFormat("en", { month: "long", timeZone: "UTC" }).format(date);
  return `${weekday}, ${day.toString()} ${monthName}`;
}

function isSessionConflict(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    ["SESSION_VERSION_CONFLICT", "SESSION_NOT_FOUND", "SESSION_TRANSITION_INVALID"].includes(
      error.problem?.code ?? "",
    )
  );
}

function RichText({ document }: { document: RichTextDocument }) {
  return <>{renderNodes(document.content)}</>;
}

function renderNodes(nodes: RichTextNode[] | undefined): React.ReactNode {
  return nodes?.map((node, index) => {
    const key = `${node.type}-${index.toString()}`;
    if (node.type === "text")
      return <Fragment key={key}>{renderMarks(node.text, node.marks)}</Fragment>;
    if (node.type === "hardBreak") return <br key={key} />;
    const content = renderNodes(node.content);
    if (node.type === "paragraph") return <p key={key}>{content}</p>;
    if (node.type === "heading")
      return node.attrs.level === 2 ? <h2 key={key}>{content}</h2> : <h3 key={key}>{content}</h3>;
    if (node.type === "bulletList") return <ul key={key}>{content}</ul>;
    if (node.type === "orderedList") return <ol key={key}>{content}</ol>;
    if (node.type === "listItem") return <li key={key}>{content}</li>;
    return <blockquote key={key}>{content}</blockquote>;
  });
}

function renderMarks(value: string, marks: RichTextMark[] | undefined): React.ReactNode {
  return (
    marks?.reduce<React.ReactNode>((content, mark, index) => {
      if (mark.type === "bold") return <strong key={index}>{content}</strong>;
      if (mark.type === "italic") return <em key={index}>{content}</em>;
      return (
        <a key={index} href={mark.attrs.href} target="_blank" rel="noopener noreferrer">
          {content}
        </a>
      );
    }, value) ?? value
  );
}

function trustedVideoEmbed(rawUrl: string): string | null {
  const youtubeEmbed = youtubeEmbedUrl(rawUrl);
  if (youtubeEmbed) return youtubeEmbed;
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return null;
  }
  const host = url.hostname.toLowerCase().replace(/^www\./, "");
  if (host === "vimeo.com") {
    const vimeoId = url.pathname.split("/").filter(Boolean)[0];
    if (vimeoId && /^\d{5,12}$/.test(vimeoId)) return `https://player.vimeo.com/video/${vimeoId}`;
  }
  return null;
}

async function invalidateLearning(
  queryClient: ReturnType<typeof useQueryClient>,
  activityId: string,
) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: activityDetailKey(activityId) }),
    queryClient.invalidateQueries({ queryKey: libraryQueryRoot }),
    queryClient.invalidateQueries({ queryKey: ["today"] }),
    queryClient.invalidateQueries({ queryKey: ["calendar"] }),
  ]);
}

function formatSessionDate(session: ActivitySession): string {
  const [year, month, day] = session.scheduledDate.split("-").map(Number);
  const date =
    year && month && day
      ? new Intl.DateTimeFormat(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric",
          timeZone: "UTC",
        }).format(new Date(Date.UTC(year, month - 1, day)))
      : session.scheduledDate;
  return session.scheduledTime ? `${date} at ${session.scheduledTime}` : date;
}

function fileTypeLabel(contentType: string): string {
  if (contentType === "application/pdf") return "PDF";
  if (contentType.includes("word")) return "Word document";
  if (contentType.includes("presentation") || contentType.includes("powerpoint")) return "Slides";
  if (contentType === "text/markdown") return "Markdown";
  if (contentType === "text/plain") return "Text";
  return "File";
}

function formatBytes(bytes: number): string {
  if (bytes < 1_024) return `${bytes} B`;
  if (bytes < 1_024 * 1_024) return `${Math.round(bytes / 1_024)} KB`;
  return `${(bytes / (1_024 * 1_024)).toFixed(1)} MB`;
}

function formatLabel(value: string): string {
  return value === "other"
    ? "Learning activity"
    : `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
}

function isActivityRouteState(value: unknown): value is ActivityRouteState {
  return typeof value === "object" && value !== null;
}
